// src/components/inventory/InventoryTable.js
// ✅ UPDATED: Collapsible filter panel (keeps header slim)
// ✅ ADDED: Stock filter (All / In Stock / Out of Stock)
// ✅ ADDED: Images filter (All / Has Images / No Images)
// ✅ ADDED: Active filter chips (only show when filters are active)
// ✅ PRESERVED: All bulk operations, column picker, parsing, and table behavior

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getFirestore,
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  deleteDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import {
  SlidersHorizontal,
  Search,
  Plus,
  Pencil,
  X,
  Eye,
  EyeOff,
  Star,
  Flame,
  Package,
  Shield,
  Ban,
  CheckSquare,
  Trash2,
  Wand2,
  Archive,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";

import InventoryEditPanel from "./InventoryEditPanel";
import ProductParsingPanel from "./ProductParsingPanel";
import { buildInventoryCategorySuggestions } from "../../utils/inventoryCategorySuggestions";
import { moveDocToRecycleBin } from "../../utils/recycleBinService";

const BUILT_IN_COLUMNS = [
  { id: "select", label: "", kind: "builtin" },
  { id: "product", label: "Product", kind: "builtin" },
  { id: "sku", label: "SKU", kind: "builtin" },
  { id: "category", label: "Category", kind: "builtin" },
  { id: "pack", label: "Pack", kind: "builtin" },
  { id: "price", label: "Price", kind: "builtin", restrictedToAdmins: true },
  { id: "stock", label: "Stock", kind: "builtin" },
  { id: "updatedAt", label: "Updated", kind: "builtin" },
  { id: "updatedBy", label: "Updated By", kind: "builtin" },
  { id: "status", label: "Status", kind: "builtin" },
  { id: "actions", label: "Actions", kind: "builtin" },
];

// Robust boolean coercion
const asBool = (v) => {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "string") return v.trim().toLowerCase() === "true";
  return false;
};

// ✅ Robust "has images" detector (supports many common schemas)
const itemHasImages = (item) => {
  if (!item) return false;

  const singleUrlFields = [
    item.imageUrl,
    item.imageURL,
    item.image,
    item.thumbnailUrl,
    item.thumbnailURL,
    item.thumbUrl,
    item.photoUrl,
    item.photoURL,
    item.primaryImageUrl,
    item.primaryImageURL,
    item.imagePath,
  ];

  if (singleUrlFields.some((v) => typeof v === "string" && v.trim().length > 0)) {
    return true;
  }

  const arrayFields = [
    item.images,
    item.imageUrls,
    item.imageURLs,
    item.photos,
    item.photoUrls,
    item.gallery,
    item.imagePaths,
  ];

  for (const arr of arrayFields) {
    if (!arr) continue;

    if (Array.isArray(arr)) {
      if (arr.some((v) => typeof v === "string" && v.trim().length > 0)) return true;

      if (
        arr.some(
          (v) =>
            v &&
            typeof v === "object" &&
            typeof v.url === "string" &&
            v.url.trim().length > 0
        )
      ) {
        return true;
      }
    }

    if (typeof arr === "object" && typeof arr.url === "string" && arr.url.trim().length > 0) {
      return true;
    }
  }

  return false;
};

const normalizeCustomColumnKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_");

const buildNeedsReviewSignature = ({ missingCoreFields = [], duplicateName = false, duplicateSku = false }) => {
  const parts = [];
  if (missingCoreFields.length) {
    parts.push(`missing:${[...missingCoreFields].sort().join(",")}`);
  }
  if (duplicateName) parts.push("duplicate-name");
  if (duplicateSku) parts.push("duplicate-sku");
  return parts.join("|");
};

const batchInChunks = (items, size = 300) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const InventoryTable = ({
  inventory,
  companyId,
  onInventoryUpdate,
  getProductSampleCount,
  userRole,
  companySettings,
  currentUserId,
  headerActions = null,
  onEditColumns = null,
  openEditItemId = null,
  onCloseEditItem = null,
}) => {
  const db = getFirestore();

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    sampleable: "all",
    stock: "all",   // all | inStock | outOfStock
    images: "all",  // all | hasImages | noImages
    archived: "active", // active | archived | all
    lowStockOnly: false,
    zeroStockOnly: false,
    hasSamplesOnly: false,
  });
  const [quickView, setQuickView] = useState("allActive");

  const handleQuickViewChange = (viewId) => {
    setQuickView(viewId);
    setFilters((prev) => {
      if (viewId === "archived") {
        return { ...prev, archived: "archived" };
      }
      if (viewId === "allActive") {
        return { ...prev, archived: "active" };
      }
      if (prev.archived === "archived") {
        return { ...prev, archived: "active" };
      }
      return prev;
    });
  };

  // Edit panel state
  const [editingItem, setEditingItem] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const editSourceRef = useRef("local");
  const dismissedUrlItemIdRef = useRef("");

  // Operation states
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [operationError, setOperationError] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOperation, setBulkOperation] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    category: "",
    sampleable: "",
    customerVisibility: "",
  });
  const [smartCategorizeOpen, setSmartCategorizeOpen] = useState(false);
  const [smartCategoryRows, setSmartCategoryRows] = useState([]);
  const [smartSelectedIds, setSmartSelectedIds] = useState(new Set());

  // Column visibility / custom fields
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    BUILT_IN_COLUMNS.forEach((col) => {
      initial[col.id] =
        col.id === "brand" || col.id === "category" || col.id === "updatedAt" || col.id === "updatedBy"
          ? false
          : true;
    });
    return initial;
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);
  const [addColumnError, setAddColumnError] = useState("");
  const [addColumnForm, setAddColumnForm] = useState({
    label: "",
    type: "text",
  });
  const [showDesktopSearch, setShowDesktopSearch] = useState(
    Boolean((filters.search || "").trim())
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const columnPickerRef = useRef(null);
  const columnPickerButtonRef = useRef(null);
  const filterPickerRef = useRef(null);
  const filterPickerButtonRef = useRef(null);
  const desktopSearchInputRef = useRef(null);

  // Product parsing states
  const [showBulkAnalysis, setShowBulkAnalysis] = useState(false);
  const [bulkAnalysisProducts, setBulkAnalysisProducts] = useState([]);
  const [currentBulkIndex, setCurrentBulkIndex] = useState(0);

  // Role check
  const isAdminOrOwner = useMemo(() => {
    const role = String(userRole || "").trim().toLowerCase();
    return role === "owner" || role === "admin" || role === "super_admin";
  }, [userRole]);

  const showPricingFlag = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    const v = flags.showInventoryPricingToEndUsers ?? cs.showInventoryPricingToEndUsers;
    return asBool(v);
  }, [companySettings]);

  const onlyOwnerCanSeePricing = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    return asBool(flags.onlyOwnerCanSeeInventoryPricing);
  }, [companySettings]);

  const isOwner = useMemo(() => {
    const role = String(userRole || "").trim().toLowerCase();
    return role === "owner";
  }, [userRole]);

  const canSeePricing = useMemo(() => {
    if (onlyOwnerCanSeePricing) return isOwner;
    if (isAdminOrOwner) return true;
    return showPricingFlag === true;
  }, [isAdminOrOwner, isOwner, onlyOwnerCanSeePricing, showPricingFlag]);

  const canManageInventory = isAdminOrOwner;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && showDesktopSearch && desktopSearchInputRef.current) {
      desktopSearchInputRef.current.focus();
    }
  }, [isMobile, showDesktopSearch]);

  useEffect(() => {
    if (!showColumnPicker) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (columnPickerRef.current?.contains(target)) return;
      if (columnPickerButtonRef.current?.contains(target)) return;
      setShowColumnPicker(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showColumnPicker]);

  useEffect(() => {
    if (!showFilterPicker) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (filterPickerRef.current?.contains(target)) return;
      if (filterPickerButtonRef.current?.contains(target)) return;
      setShowFilterPicker(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showFilterPicker]);

  // Unique categories
  const categories = useMemo(
    () => [...new Set((inventory || []).map((i) => i.category).filter(Boolean))].sort(),
    [inventory]
  );

  const duplicateNameMap = useMemo(() => {
    const counts = new Map();
    (Array.isArray(inventory) ? inventory : []).forEach((item) => {
      const key = String(item?.name || "").trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [inventory]);

  const duplicateSkuMap = useMemo(() => {
    const counts = new Map();
    (Array.isArray(inventory) ? inventory : []).forEach((item) => {
      const key = String(item?.sku || "").trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [inventory]);

  const getMissingCoreFields = useCallback((item) => {
    const missing = [];
    if (!String(item?.name || "").trim()) missing.push("name");
    if (!String(item?.category || "").trim()) missing.push("category");
    if (!String(item?.packSize || "").trim()) missing.push("pack size");
    return missing;
  }, []);

  const hasDuplicateName = useCallback((item) => {
    const key = String(item?.name || "").trim().toLowerCase();
    return key ? (duplicateNameMap.get(key) || 0) > 1 : false;
  }, [duplicateNameMap]);

  const hasDuplicateSku = useCallback((item) => {
    const key = String(item?.sku || "").trim().toLowerCase();
    return key ? (duplicateSkuMap.get(key) || 0) > 1 : false;
  }, [duplicateSkuMap]);

  const getNeedsReviewState = useCallback((item) => {
    const missingCoreFields = getMissingCoreFields(item);
    const duplicateName = hasDuplicateName(item);
    const duplicateSku = hasDuplicateSku(item);
    const signature = buildNeedsReviewSignature({
      missingCoreFields,
      duplicateName,
      duplicateSku,
    });
    const dismissedSignature = String(item?.needsReviewDismissedSignature || "").trim();
    return {
      missingCoreFields,
      duplicateName,
      duplicateSku,
      signature,
      actionable: Boolean(signature) && signature !== dismissedSignature,
    };
  }, [getMissingCoreFields, hasDuplicateName, hasDuplicateSku]);

  const quickViewOptions = useMemo(() => {
    const list = Array.isArray(inventory) ? inventory : [];
    const counts = {
      allActive: list.filter((item) => item.archived !== true).length,
      needsReview: list.filter((item) => getNeedsReviewState(item).actionable).length,
      customerVisible: list.filter((item) => item.archived !== true && item.customerVisible === true).length,
      seasonal: list.filter((item) => item.archived !== true && item.seasonal === true).length,
      contract: list.filter((item) => item.archived !== true && item.contractItem === true).length,
      archived: list.filter((item) => item.archived === true).length,
    };

    return [
      { id: "allActive", label: "All Active", count: counts.allActive },
      { id: "needsReview", label: "Needs Review", count: counts.needsReview },
      { id: "customerVisible", label: "Customer Visible", count: counts.customerVisible },
      { id: "seasonal", label: "Seasonal", count: counts.seasonal },
      { id: "contract", label: "Contract", count: counts.contract },
      { id: "archived", label: "Archived", count: counts.archived },
    ];
  }, [inventory, getNeedsReviewState]);

  // Load inventory field definitions
  useEffect(() => {
    const loadDefs = async () => {
      if (!companyId) return;

      try {
        const colRef = collection(db, "companies", companyId, "inventoryFieldDefinitions");
        const snap = await getDocs(colRef);
        const defs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        defs.sort((a, b) => {
          const orderA = typeof a.order === "number" ? a.order : 9999;
          const orderB = typeof b.order === "number" ? b.order : 9999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase());
        });

        setFieldDefinitions(defs);

        setColumnVisibility((prev) => {
          const next = { ...prev };
          defs.forEach((def) => {
            const colId = `custom:${def.key}`;
            if (!(colId in next)) next[colId] = !!def.showInTable;
          });
          return next;
        });
      } catch (err) {
        console.error("Error loading inventory field definitions for table:", err);
      }
    };

    loadDefs();
  }, [companyId, db]);

  // Apply filters
  const filteredInventory = useMemo(() => {
    const list = Array.isArray(inventory) ? inventory : [];

    return list.filter((item) => {
      const s = (filters.search || "").toLowerCase();
      const matchesSearch =
        !s ||
        item.name?.toLowerCase().includes(s) ||
        item.sku?.toLowerCase().includes(s);

      const matchesCategory = !filters.category || item.category === filters.category;

      const matchesSampleable =
        filters.sampleable === "all" ||
        (filters.sampleable === "sampleable" && item.sampleable) ||
        (filters.sampleable === "notSampleable" && !item.sampleable);

      const stockNum = Number(item?.stock ?? 0) || 0;
      const lowStockThreshold = Number(item?.lowStockThreshold ?? 10) || 10;
      const sampleCount = Number(getProductSampleCount(item.id) || 0);
      const matchesStock =
        filters.stock === "all" ||
        (filters.stock === "inStock" && stockNum > 0) ||
        (filters.stock === "outOfStock" && stockNum <= 0);
      const matchesLowStock = !filters.lowStockOnly || (stockNum > 0 && stockNum <= lowStockThreshold);
      const matchesZeroStock = !filters.zeroStockOnly || stockNum <= 0;
      const matchesSamples = !filters.hasSamplesOnly || sampleCount > 0;

      const hasImg = itemHasImages(item);
      const matchesImages =
        filters.images === "all" ||
        (filters.images === "hasImages" && hasImg) ||
        (filters.images === "noImages" && !hasImg);

      const isArchived = item.archived === true;
      const matchesArchived =
        filters.archived === "all" ||
        (filters.archived === "active" && !isArchived) ||
        (filters.archived === "archived" && isArchived);

      const shouldBypassAllActiveQuickView = quickView === "allActive" && filters.archived !== "active";
      const matchesQuickView =
        shouldBypassAllActiveQuickView
          ? true
          : quickView === "allActive"
          ? !isArchived
          : quickView === "needsReview"
          ? getNeedsReviewState(item).actionable
          : quickView === "customerVisible"
          ? !isArchived && item.customerVisible === true
          : quickView === "seasonal"
          ? !isArchived && item.seasonal === true
          : quickView === "contract"
          ? !isArchived && item.contractItem === true
          : quickView === "archived"
          ? isArchived
          : true;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesSampleable &&
        matchesStock &&
        matchesLowStock &&
        matchesZeroStock &&
        matchesSamples &&
        matchesImages &&
        matchesArchived &&
        matchesQuickView
      );
    });
  }, [inventory, filters, quickView, getNeedsReviewState, getProductSampleCount]);

  // Clear selection when filters change
  useEffect(() => {
    if (!canManageInventory && selectedIds.size > 0) {
      setSelectedIds(new Set());
    }
  }, [canManageInventory, selectedIds.size]);

  useEffect(() => {
    if (selectedIds.size > 0) setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.category, filters.sampleable, filters.stock, filters.images, filters.lowStockOnly, filters.zeroStockOnly, filters.hasSamplesOnly]);

  useEffect(() => {
    if (isMobile && selectedIds.size > 0) {
      setSelectedIds(new Set());
    }
  }, [isMobile, selectedIds]);

  // Hot threshold
  const sampleCounts = useMemo(
    () => (Array.isArray(inventory) ? inventory : []).map((item) => getProductSampleCount(item.id)),
    [inventory, getProductSampleCount]
  );
  const maxSamples = sampleCounts.length ? Math.max(...sampleCounts) : 0;
  const hotThreshold = Math.max(1, Math.floor(maxSamples * 0.2));

  const isLowStock = (item) => {
    const stock = Number(item?.stock ?? 0) || 0;
    const threshold = Number(item?.lowStockThreshold ?? 10) || 10;
    return stock > 0 && stock <= threshold;
  };
  const isHotProduct = (itemId) => getProductSampleCount(itemId) >= hotThreshold;
  const isAdminsOnlyForEndUsers = (item) =>
    Array.isArray(item.groupIds) ? item.groupIds.length === 0 : true;
  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (!canManageInventory) return;
    if (selectedIds.size === filteredInventory.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInventory.map((item) => item.id)));
    }
  };

  const toggleSelectItem = (itemId) => {
    if (!canManageInventory) return;
    const newSelected = new Set(selectedIds);
    if (newSelected.has(itemId)) newSelected.delete(itemId);
    else newSelected.add(itemId);
    setSelectedIds(newSelected);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const closeBulkEdit = () => {
    if (bulkEditSaving) return;
    setBulkEditOpen(false);
  };

  const runBulkEdit = async () => {
    if (!canManageInventory) {
      setOperationError("Only admins can bulk edit inventory.");
      return;
    }
    if (!companyId || selectedIds.size === 0) return;

    const updates = {};
    if (bulkEditForm.category === "__clear__") updates.category = null;
    else if (bulkEditForm.category) updates.category = bulkEditForm.category;

    if (bulkEditForm.sampleable === "sampleable") {
      updates.sampleable = true;
      updates.unsampleable = false;
    } else if (bulkEditForm.sampleable === "notSampleable") {
      updates.sampleable = false;
      updates.unsampleable = true;
    }

    if (bulkEditForm.customerVisibility === "show") {
      updates.customerVisible = true;
      updates.hiddenFromCustomer = false;
    } else if (bulkEditForm.customerVisibility === "hide") {
      updates.customerVisible = false;
      updates.hiddenFromCustomer = true;
    }

    if (!Object.keys(updates).length) {
      setOperationError("Choose at least one inventory field to update.");
      return;
    }

    setBulkEditSaving(true);
    setOperationError(null);

    try {
      const batch = writeBatch(db);
      const timestamp = serverTimestamp();
      const uid = currentUserId || null;
      for (const itemId of selectedIds) {
        const itemRef = doc(db, "companies", companyId, "inventory", itemId);
        batch.update(itemRef, {
          ...updates,
          updatedAt: timestamp,
          updatedBy: uid,
        });
      }
      await batch.commit();
      setBulkEditOpen(false);
      setBulkEditForm({ category: "", sampleable: "", customerVisibility: "" });
      clearSelection();
      onInventoryUpdate();
    } catch (err) {
      console.error("[InventoryTable] bulk edit error:", err);
      setOperationError("Failed to bulk edit inventory. Please try again.");
    } finally {
      setBulkEditSaving(false);
    }
  };

  const openSmartCategorize = () => {
    if (!canManageInventory || selectedIds.size === 0) return;
    const selectedProducts = inventory.filter((item) => selectedIds.has(item.id));
    const suggestions = buildInventoryCategorySuggestions(selectedProducts);
    if (!suggestions.length) {
      setOperationError("No smart category suggestions were found for the selected items.");
      return;
    }
    setOperationError(null);
    setSmartCategoryRows(suggestions);
    setSmartSelectedIds(new Set(suggestions.map((row) => row.itemId).filter(Boolean)));
    setSmartCategorizeOpen(true);
  };

  const toggleSmartRow = (itemId) => {
    setSmartSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const approveSmartCategorization = async () => {
    if (!companyId || smartSelectedIds.size === 0) return;
    setBulkEditSaving(true);
    setOperationError(null);
    try {
      const batch = writeBatch(db);
      const timestamp = serverTimestamp();
      const uid = currentUserId || null;
      smartCategoryRows
        .filter((row) => smartSelectedIds.has(row.itemId))
        .forEach((row) => {
          const itemRef = doc(db, "companies", companyId, "inventory", row.itemId);
          batch.update(itemRef, {
            category: row.recommendedCategory || null,
            subCategory: row.recommendedSubCategory || null,
            updatedAt: timestamp,
            updatedBy: uid,
            categoryRecommendationAppliedAt: timestamp,
            categoryRecommendationMatchedTerm: row.matchedTerm || null,
          });
        });
      await batch.commit();
      setSmartCategorizeOpen(false);
      setBulkEditOpen(false);
      setSmartCategoryRows([]);
      clearSelection();
      onInventoryUpdate();
    } catch (err) {
      console.error("[InventoryTable] smart categorize error:", err);
      setOperationError("Failed to apply smart category updates. Please try again.");
    } finally {
      setBulkEditSaving(false);
    }
  };

  // Bulk operations
  const handleBulkOperation = async (operation) => {
    if (!canManageInventory) {
      setOperationError("Only admins can bulk edit inventory.");
      return;
    }
    if (!companyId || selectedIds.size === 0) return;

    setBulkOperation(operation);
    setOperationError(null);

    try {
      const uid = currentUserId || null;
      const selectedIdList = Array.from(selectedIds);
      const inventoryById = new Map((Array.isArray(inventory) ? inventory : []).map((item) => [item.id, item]));
      const chunkSize = operation === "delete" ? 125 : 350;

      for (const chunk of batchInChunks(selectedIdList, chunkSize)) {
        const batch = writeBatch(db);
        const timestamp = serverTimestamp();

        for (const itemId of chunk) {
          const itemRef = doc(db, "companies", companyId, "inventory", itemId);

          switch (operation) {
            case "hide":
              batch.update(itemRef, {
                customerVisible: false,
                hiddenFromCustomer: true,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            case "show":
              batch.update(itemRef, {
                customerVisible: true,
                hiddenFromCustomer: false,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            case "makeSampleable":
              batch.update(itemRef, {
                sampleable: true,
                unsampleable: false,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            case "makeNotSampleable":
              batch.update(itemRef, {
                sampleable: false,
                unsampleable: true,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            case "delete": {
              const item = inventoryById.get(itemId);
              if (!item) break;
              await moveDocToRecycleBin({
                db,
                companyId,
                entityType: "inventory",
                entityId: itemId,
                data: item,
                displayName: item.name || item.sku || itemId,
                deletedByUid: uid || null,
                deletedByRole: userRole || null,
                metadata: { source: "bulkDelete" },
              });
              batch.delete(itemRef);
              break;
            }
            case "archive":
              batch.update(itemRef, {
                archived: true,
                archivedAt: timestamp,
                archivedBy: uid,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            case "unarchive":
              batch.update(itemRef, {
                archived: false,
                archivedAt: null,
                archivedBy: null,
                updatedAt: timestamp,
                updatedBy: uid,
              });
              break;
            default:
              break;
          }
        }

        await batch.commit();
      }
      clearSelection();
      onInventoryUpdate?.();
    } catch (err) {
      console.error(`Bulk ${operation} error:`, err);
      setOperationError(`Failed to perform bulk ${operation}. Please try again.`);
    } finally {
      setBulkOperation(null);
    }
  };

  // Clear any error after 5 seconds
  useEffect(() => {
    if (!operationError) return;
    const timer = setTimeout(() => setOperationError(null), 5000);
    return () => clearTimeout(timer);
  }, [operationError]);

  const handleFirestoreError = (err, operation) => {
    console.error(`Error during ${operation}:`, err);

    if (err?.code === "permission-denied") {
      setOperationError(`You don't have permission to ${operation}. Only admins can perform this action.`);
      return;
    }
    setOperationError(`Failed to ${operation}. Please try again.`);
  };

  const toggleSampleable = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can edit inventory.");
      return;
    }
    if (!companyId || togglingId) return;

    setTogglingId(item.id);
    setOperationError(null);

    try {
      await updateDoc(doc(db, "companies", companyId, "inventory", item.id), {
        sampleable: !item.sampleable,
        unsampleable: !item.sampleable,
        updatedAt: serverTimestamp(),
      });
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, "toggle sampleable status");
    } finally {
      setTogglingId(null);
    }
  };

  const toggleCustomerVisible = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can edit inventory.");
      return;
    }
    if (!companyId || togglingId) return;

    const current = item.customerVisible === true;
    setTogglingId(item.id);
    setOperationError(null);

    try {
      await updateDoc(doc(db, "companies", companyId, "inventory", item.id), {
        customerVisible: !current,
        hiddenFromCustomer: current,
        updatedAt: serverTimestamp(),
      });
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, "toggle customer visibility");
    } finally {
      setTogglingId(null);
    }
  };

  const toggleFeatured = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can edit inventory.");
      return;
    }
    if (!companyId || togglingId) return;

    setTogglingId(item.id);
    setOperationError(null);

    try {
      await updateDoc(doc(db, "companies", companyId, "inventory", item.id), {
        featured: !item.featured,
        updatedAt: serverTimestamp(),
      });
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, "toggle featured status");
    } finally {
      setTogglingId(null);
    }
  };

  const toggleArchived = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can edit inventory.");
      return;
    }
    if (!companyId || togglingId) return;

    const nextArchived = !(item.archived === true);
    setTogglingId(item.id);
    setOperationError(null);

    try {
      await updateDoc(doc(db, "companies", companyId, "inventory", item.id), {
        archived: nextArchived,
        archivedAt: nextArchived ? serverTimestamp() : null,
        archivedBy: nextArchived ? (currentUserId || null) : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId || null,
      });
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, nextArchived ? "archive item" : "unarchive item");
    } finally {
      setTogglingId(null);
    }
  };

  const clearNeedsReview = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can edit inventory.");
      return;
    }
    if (!companyId || togglingId) return;

    const reviewState = getNeedsReviewState(item);
    if (!reviewState.actionable) return;

    setTogglingId(item.id);
    setOperationError(null);

    try {
      await updateDoc(doc(db, "companies", companyId, "inventory", item.id), {
        needsReviewDismissedSignature: reviewState.signature,
        needsReviewDismissedAt: serverTimestamp(),
        needsReviewDismissedBy: currentUserId || null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId || null,
      });
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, "clear needs review warning");
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditClick = (item) => {
    if (!canManageInventory) return;
    editSourceRef.current = "local";
    dismissedUrlItemIdRef.current = "";
    setEditingItem(item);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    const closingId = String(editingItem?.id || openEditItemId || "").trim();
    setIsEditOpen(false);
    setEditingItem(null);
    if (editSourceRef.current === "url" && onCloseEditItem) {
      dismissedUrlItemIdRef.current = closingId;
      onCloseEditItem();
    }
    editSourceRef.current = "local";
  };

  const handleEditUpdated = () => onInventoryUpdate();

  useEffect(() => {
    const id = String(openEditItemId || "").trim();
    if (!id) return;
    if (dismissedUrlItemIdRef.current === id) return;
    if (isEditOpen && editingItem?.id === id) return;

    const fromList = (Array.isArray(inventory) ? inventory : []).find((item) => item?.id === id);
    if (!fromList) return;

    editSourceRef.current = "url";
    dismissedUrlItemIdRef.current = "";
    setEditingItem(fromList);
    setIsEditOpen(true);
  }, [openEditItemId, inventory, isEditOpen, editingItem]);

  const handleDelete = async (item) => {
    if (!canManageInventory) {
      setOperationError("Only admins can delete inventory.");
      return;
    }
    if (!companyId || !item?.id || deletingId) return;

    const label = item.name || item.sku || "this item";
    const ok = window.confirm(`Move ${label} to the recycle bin? You can restore it later from Admin.`);
    if (!ok) return;

    setDeletingId(item.id);
    setOperationError(null);

    try {
      await addDoc(collection(db, "companies", companyId, "inventory", item.id, "changelog"), {
        changedAt: serverTimestamp(),
        changedBy: "user",
        type: "deleted",
        oldValues: item,
        source: "inventoryTableDelete",
      });

      await moveDocToRecycleBin({
        db,
        companyId,
        entityType: "inventory",
        entityId: item.id,
        data: item,
        displayName: item.name || item.sku || item.id,
        deletedByUid: currentUserId || null,
        deletedByRole: userRole || null,
        metadata: { source: "inventoryTableDelete" },
      });

      await deleteDoc(doc(db, "companies", companyId, "inventory", item.id));
      onInventoryUpdate();
    } catch (err) {
      handleFirestoreError(err, "delete item");
    } finally {
      setDeletingId(null);
    }
  };

  // Product parsing handlers
  const handleBulkParseClick = () => {
    if (!canManageInventory) return;
    if (selectedIds.size === 0) return;
    const selectedProducts = inventory.filter((item) => selectedIds.has(item.id));
    setBulkAnalysisProducts(selectedProducts);
    setCurrentBulkIndex(0);
    setShowBulkAnalysis(true);
  };

  const handleParserApply = async (fixedProduct) => {
    if (!companyId) return;

    try {
      const uid = currentUserId || null;
      const itemRef = doc(db, "companies", companyId, "inventory", fixedProduct.id);

      await updateDoc(itemRef, {
        name: fixedProduct.name.trim(),
        packSize: fixedProduct.packSize?.trim() || null,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });

      if (currentBulkIndex + 1 < bulkAnalysisProducts.length) {
        setCurrentBulkIndex((prev) => prev + 1);
      } else {
        setShowBulkAnalysis(false);
        setBulkAnalysisProducts([]);
        clearSelection();
        onInventoryUpdate();
      }
    } catch (err) {
      console.error("Error applying parser fix:", err);
      setOperationError("Failed to save parsed changes. Please try again.");
    }
  };

  const handleSkipProduct = () => {
    if (currentBulkIndex + 1 < bulkAnalysisProducts.length) {
      setCurrentBulkIndex((prev) => prev + 1);
    } else {
      setShowBulkAnalysis(false);
      setBulkAnalysisProducts([]);
      clearSelection();
    }
  };

  const headerActionItems = useMemo(() => React.Children.toArray(headerActions), [headerActions]);
  const mobileSecondaryAction = headerActionItems[0] || null;
  const mobilePrimaryAction = headerActionItems[1] || null;
  const desktopActionsMenu = headerActionItems[0] || null;
  const desktopPrimaryAction = headerActionItems[1] || null;

  // Column helpers
  const customColumns = fieldDefinitions.map((def) => ({
    id: `custom:${def.key}`,
    label: def.label || def.key,
    kind: "custom",
    def,
  }));

  const allColumns = [...BUILT_IN_COLUMNS, ...customColumns];

  const visibleColumns = allColumns.filter((col) => {
    if (columnVisibility[col.id] === false) return false;
    if (col.id === "price" && !canSeePricing) return false;
    if ((col.id === "select" || col.id === "actions") && !canManageInventory) return false;
    if (isMobile && ["select", "actions", "category", "pack", "price", "sku"].includes(col.id)) return false;
    return true;
  });

  const toggleColumn = (columnId, visible) => {
    setColumnVisibility((prev) => ({ ...prev, [columnId]: visible }));
  };

  const resetAddColumnModal = () => {
    setShowAddColumnModal(false);
    setAddColumnError("");
    setAddColumnForm({
      label: "",
      type: "text",
    });
  };

  const handleCreateColumn = async () => {
    if (!companyId || addingColumn) return;

    const label = String(addColumnForm.label || "").trim();
    const key = normalizeCustomColumnKey(label);

    if (!label) {
      setAddColumnError("Column name is required.");
      return;
    }

    if (!key) {
      setAddColumnError("Column key could not be created from that name.");
      return;
    }

    const keyExists = fieldDefinitions.some(
      (def) => String(def?.key || "").toLowerCase() === key.toLowerCase()
    );
    if (keyExists) {
      setAddColumnError("A column with that name already exists.");
      return;
    }

    setAddingColumn(true);
    setAddColumnError("");

    try {
      const order = fieldDefinitions.length
        ? Math.max(
            ...fieldDefinitions.map((def) =>
              typeof def?.order === "number" ? def.order : 0
            )
          ) + 1
        : 1;

      const data = {
        key,
        label,
        type: addColumnForm.type,
        showInTable: true,
        showInEdit: true,
        showInCatalog: false,
        options: [],
        order,
        createdAt: Date.now(),
      };

      const colRef = collection(db, "companies", companyId, "inventoryFieldDefinitions");
      const docRef = await addDoc(colRef, data);
      const nextDef = { id: docRef.id, ...data };

      setFieldDefinitions((prev) => {
        const next = [...prev, nextDef];
        next.sort((a, b) => {
          const orderA = typeof a.order === "number" ? a.order : 9999;
          const orderB = typeof b.order === "number" ? b.order : 9999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase());
        });
        return next;
      });
      setColumnVisibility((prev) => ({
        ...prev,
        [`custom:${key}`]: true,
      }));
      resetAddColumnModal();
    } catch (err) {
      console.error("[InventoryTable] Failed to create custom column:", err);
      setAddColumnError("Failed to add column.");
    } finally {
      setAddingColumn(false);
    }
  };

  const getHeaderClass = (colId) => {
    const base =
      "border-r border-sky-100 last:border-r-0 text-slate-800 font-semibold bg-gradient-to-b from-sky-50 via-white to-white";
    if (colId === "select") return `px-2 py-2 text-center w-10 ${base}`;
    if (colId === "product") return `${isMobile ? "px-2 py-2" : "px-3 py-2"} text-left min-w-[360px] ${base}`;
    if (colId === "price" || colId === "stock") return `${isMobile ? "px-2 py-2" : "px-3 py-2"} text-right ${base}`;
    if (colId === "status" || colId === "actions") return `${isMobile ? "px-2 py-2" : "px-3 py-2"} text-center ${base}`;
    return `${isMobile ? "px-2 py-2" : "px-3 py-2"} text-left ${base}`;
  };

  const getCellClass = (colId) => {
    const base = "border-r border-slate-100 last:border-r-0";
    if (colId === "select") return `px-2 py-1.5 text-center w-10 ${base}`;
    if (colId === "product") return `${isMobile ? "px-2 py-1.5" : "px-3 py-1.5"} text-gray-700 min-w-[360px] ${base}`;
    if (colId === "price" || colId === "stock") return `${isMobile ? "px-2 py-1.5" : "px-3 py-1.5"} text-right text-gray-700 ${base}`;
    if (colId === "status" || colId === "actions") return `${isMobile ? "px-2 py-1.5" : "px-3 py-1.5"} text-center ${base}`;
    return `${isMobile ? "px-2 py-1.5" : "px-3 py-1.5"} text-gray-700 ${base}`;
  };

  const IconButton = ({ title, onClick, disabled, active, children, tone = "slate" }) => {
    const toneMap = {
      slate: active
        ? "bg-slate-900 text-white border-slate-900"
        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
      blue: active
        ? "bg-blue-600 text-white border-blue-600"
        : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50",
      yellow: active
        ? "bg-yellow-500 text-white border-yellow-500"
        : "bg-white text-yellow-700 border-yellow-200 hover:bg-yellow-50",
      emerald: active
        ? "bg-emerald-600 text-white border-emerald-600"
        : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
    };

    return (
      <button
        type="button"
        title={title}
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ${
          toneMap[tone] || toneMap.slate
        } ${disabled ? "opacity-50 cursor-wait" : ""}`}
      >
        {children}
      </button>
    );
  };

  const renderCell = (col, item, sampleCount) => {
    const adminsOnly = isAdminsOnlyForEndUsers(item);
    const unsampleable = item.unsampleable === true;
    const customerVisible = item.customerVisible === true;
    const hiddenFromCustomers = !customerVisible;
    const isToggling = togglingId === item.id;
    const isArchived = item.archived === true;
    const reviewState = getNeedsReviewState(item);
    const missingCoreFields = reviewState.missingCoreFields;
    const updatedAtDate =
      typeof item?.updatedAt?.toDate === "function"
        ? item.updatedAt.toDate()
        : item?.updatedAt?.seconds
        ? new Date(item.updatedAt.seconds * 1000)
        : item?.updatedAt
        ? new Date(item.updatedAt)
        : null;

    switch (col.id) {
      case "select":
        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={selectedIds.has(item.id)}
              onChange={() => toggleSelectItem(item.id)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
          </div>
        );

      case "product":
        return (
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-1 shrink-0">
              {isLowStock(item) && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-red-200 bg-red-50 text-red-700"
                  title="Low Stock"
                >
                  <Package className="w-3 h-3" />
                </span>
              )}

              {item.featured && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-yellow-200 bg-yellow-50 text-yellow-700"
                  title="Featured"
                >
                  <Star className="w-3 h-3" />
                </span>
              )}

              {isHotProduct(item.id) && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-orange-200 bg-orange-50 text-orange-700"
                  title="Hot Product"
                >
                  <Flame className="w-3 h-3" />
                </span>
              )}

              {hiddenFromCustomers && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-slate-200 bg-slate-50 text-slate-600"
                  title="Hidden from Customers"
                >
                  <EyeOff className="w-3 h-3" />
                </span>
              )}

              {adminsOnly && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-slate-200 bg-slate-50 text-slate-700"
                  title="Hidden from End Users (no groups)"
                >
                  <Shield className="w-3 h-3" />
                </span>
              )}

              {unsampleable && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-amber-200 bg-amber-50 text-amber-700"
                  title="Temporarily unsampleable (legacy flag)"
                >
                  <Ban className="w-3 h-3" />
                </span>
              )}

              {isArchived && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-slate-300 bg-slate-100 text-slate-700"
                  title="Archived"
                >
                  <Archive className="w-3 h-3" />
                </span>
              )}

              {reviewState.actionable && missingCoreFields.length > 0 && (
                <span
                  className="inventory-table-icon-badge inline-flex items-center justify-center h-5 w-5 rounded border border-amber-200 bg-amber-50 text-amber-700"
                  title={`Missing core info: ${missingCoreFields.join(", ")}`}
                >
                  <AlertTriangle className="w-3 h-3" />
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="block min-w-0 flex-1 font-medium text-gray-900 truncate">{item.name || "—"}</span>
                  {sampleCount > 0 && (
                    <span className="text-[11px] text-gray-500 whitespace-nowrap shrink-0">
                      {sampleCount} sample{sampleCount === 1 ? "" : "s"}
                    </span>
                  )}
                  {isMobile && canManageInventory ? (
                    <button
                      type="button"
                      onClick={() => handleEditClick(item)}
                      title="Edit"
                      className="inline-flex items-center justify-center h-7 w-7 shrink-0 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>

            </div>
          </div>
        );

      case "brand":
        return item.brand || "—";
      case "sku":
        return item.sku || "—";
      case "category":
        return item.category || "—";
      case "pack":
        return item.packSize || "—";
      case "price":
        return item.price != null ? `$${Number(item.price).toFixed(2)}` : "—";
      case "stock":
        return (
          <span className={isLowStock(item) ? "text-red-600 font-medium" : ""}>
            {item.stock || 0}
          </span>
        );
      case "updatedAt":
        return updatedAtDate && !Number.isNaN(updatedAtDate.getTime())
          ? updatedAtDate.toLocaleDateString()
          : "—";
      case "updatedBy":
        return item.updatedBy || "—";

      case "status":
        return canManageInventory ? (
          <div className="flex items-center justify-center gap-1.5">
            <IconButton
              title={
                customerVisible
                  ? "Visible to customers (click to hide)"
                  : "Hidden from customers (click to show)"
              }
              onClick={() => toggleCustomerVisible(item)}
              disabled={isToggling}
              active={customerVisible}
              tone="blue"
            >
              {customerVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </IconButton>

            <IconButton
              title={item.featured ? "Featured (click to unfeature)" : "Not featured (click to feature)"}
              onClick={() => toggleFeatured(item)}
              disabled={isToggling}
              active={!!item.featured}
              tone="yellow"
            >
              <Star className="w-4 h-4" />
            </IconButton>

            <IconButton
              title={item.sampleable ? "Sample-able (click to turn off)" : "Not sample-able (click to turn on)"}
              onClick={() => toggleSampleable(item)}
              disabled={isToggling}
              active={!!item.sampleable}
              tone="emerald"
            >
              <Package className="w-4 h-4" />
            </IconButton>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium border ${customerVisible ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
              {customerVisible ? "Visible" : "Hidden"}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium border ${item.featured ? "bg-yellow-50 text-yellow-700 border-yellow-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
              {item.featured ? "Featured" : "Standard"}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium border ${item.sampleable ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {item.sampleable ? "Sampleable" : "Not sampleable"}
            </span>
          </div>
        );

      case "actions":
        return canManageInventory ? (
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => handleEditClick(item)}
              title="Edit"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => handleDelete(item)}
              disabled={deletingId === item.id}
              title={deletingId === item.id ? "Deleting…" : "Delete"}
              className={`inline-flex items-center justify-center h-7 w-7 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50 transition-colors ${
                deletingId === item.id ? "opacity-50 cursor-wait" : ""
              }`}
            >
              <X className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => toggleArchived(item)}
              disabled={isToggling}
              title={isArchived ? "Unarchive" : "Archive"}
              className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ${
                isArchived
                  ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              } ${isToggling ? "opacity-50 cursor-wait" : ""}`}
            >
              {isArchived ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            </button>

            {reviewState.actionable ? (
              <button
                type="button"
                onClick={() => clearNeedsReview(item)}
                disabled={isToggling}
                title="Mark current review warnings as okay"
                className={`inline-flex items-center justify-center h-7 w-7 rounded-md border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors ${
                  isToggling ? "opacity-50 cursor-wait" : ""
                }`}
              >
                <CheckSquare className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium border border-slate-200 bg-slate-50 text-slate-500">
            View only
          </span>
        );

      default:
        if (col.kind === "custom" && col.def) {
          const value = item.custom && col.def.key ? item.custom[col.def.key] : undefined;
          if (value === undefined || value === null || value === "") return "—";
          return String(value);
        }
        return null;
    }
  };

  const renderHeader = (col) => {
    if (col.id === "select") {
      return (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={selectedIds.size > 0 && selectedIds.size === filteredInventory.length}
            ref={(input) => {
              if (input) {
                input.indeterminate =
                  selectedIds.size > 0 && selectedIds.size < filteredInventory.length;
              }
            }}
            onChange={toggleSelectAll}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            title={selectedIds.size === filteredInventory.length ? "Deselect all" : "Select all"}
          />
        </div>
      );
    }
    return col.label;
  };

  return (
    <>
      <InventoryEditPanel
        isOpen={isEditOpen}
        onClose={handleEditClose}
        companyId={companyId}
        item={editingItem}
        onUpdated={handleEditUpdated}
      />

      <div className="inventory-table-shell bg-white rounded border border-gray-200">
        {operationError && (
          <div className="p-3 bg-red-50 border-b border-red-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-red-800">{operationError}</p>
              <button
                onClick={() => setOperationError(null)}
                className="text-red-700 hover:text-red-900 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Bulk action toolbar */}
        {canManageInventory && !isMobile && selectedIds.size > 0 && (
          <div className="p-3 border-b border-blue-200 bg-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-blue-700" />
                  <span className="text-sm font-medium text-blue-900">
                    {selectedIds.size} item{selectedIds.size === 1 ? "" : "s"} selected
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={openSmartCategorize}
                    disabled={bulkOperation || bulkEditSaving || selectedIds.size === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Smart Categorize
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("show")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Show to Customers
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("hide")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                    Hide from Customers
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("makeSampleable")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                  >
                    <Package className="w-3.5 h-3.5" />
                    Make Sample-able
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("makeNotSampleable")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    Make Not Sample-able
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("archive")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    Archive
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("unarchive")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Unarchive
                  </button>

                  <button
                    type="button"
                    onClick={handleBulkParseClick}
                    disabled={bulkOperation || selectedIds.size === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Parse Product Names
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBulkOperation("delete")}
                    disabled={bulkOperation}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete Selected
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-blue-700 hover:text-blue-900 font-medium transition-colors"
              >
                Clear Selection
              </button>
            </div>

            {bulkOperation && (
              <div className="mt-2 text-xs text-blue-700 flex items-center gap-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700"></div>
                Processing bulk {bulkOperation}...
              </div>
            )}
          </div>
        )}

        {/* ✅ Slim header row: Search + buttons */}
        <div className="p-3 border-b border-gray-200">
          {isMobile ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  />
                </div>
                {mobilePrimaryAction ? <div className="shrink-0">{mobilePrimaryAction}</div> : null}
              </div>

              <div className="flex items-center gap-2">
                <select
                  className="min-w-0 flex-1 h-9 rounded-lg border border-gray-300 bg-white px-3 text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={quickView}
                  onChange={(e) => handleQuickViewChange(e.target.value)}
                >
                  {quickViewOptions.map((view) => (
                    <option key={view.id} value={view.id}>
                      {view.label} ({view.count})
                    </option>
                  ))}
                </select>

                <div className="relative shrink-0">
                  <button
                    ref={filterPickerButtonRef}
                    type="button"
                    onClick={() => setShowFilterPicker((prev) => !prev)}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-[13px] font-medium whitespace-nowrap"
                    title="Inventory filters"
                  >
                    <SlidersHorizontal className="w-4 h-4 text-gray-600" />
                    Filter
                  </button>

                  {showFilterPicker && (
                    <div
                      ref={filterPickerRef}
                      className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg z-10"
                    >
                      <p className="mb-2 text-xs font-medium text-gray-500">Stock filters</p>
                      <div className="space-y-2 text-sm text-gray-700">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600"
                            checked={!!filters.lowStockOnly}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, lowStockOnly: e.target.checked }))
                            }
                          />
                          <span>Low stock</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600"
                            checked={!!filters.zeroStockOnly}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, zeroStockOnly: e.target.checked }))
                            }
                          />
                          <span>Zero stock</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600"
                            checked={!!filters.hasSamplesOnly}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, hasSamplesOnly: e.target.checked }))
                            }
                          />
                          <span>Has samples</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {mobileSecondaryAction ? <div className="shrink-0">{mobileSecondaryAction}</div> : null}
              </div>

              <div className="text-xs text-gray-500">
                {filteredInventory.length} item{filteredInventory.length !== 1 ? "s" : ""}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
              <div className="relative shrink-0">
                {showDesktopSearch ? (
                  <div className="relative w-[210px]">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-500" />
                    <input
                      ref={desktopSearchInputRef}
                      type="text"
                      placeholder="Search products..."
                      className="h-10 w-full rounded-xl border border-sky-200 bg-white pl-10 pr-9 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      value={filters.search}
                      onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, search: "" }));
                        setShowDesktopSearch(false);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      aria-label="Close product search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowDesktopSearch(true)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                    title="Search products"
                  >
                    <Search className="h-4 w-4 text-sky-500" />
                    <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                      Search
                    </span>
                  </button>
                )}
              </div>

              <select
                className="h-10 rounded-xl border border-sky-200 bg-sky-50/80 px-3 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                value={quickView}
                onChange={(e) => handleQuickViewChange(e.target.value)}
              >
                {quickViewOptions.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.label} ({view.count})
                  </option>
                ))}
              </select>

              <div className="relative">
                <button
                  ref={filterPickerButtonRef}
                  type="button"
                  onClick={() => setShowFilterPicker((prev) => !prev)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                  title="Inventory filters"
                >
                  <SlidersHorizontal className="w-4 h-4 text-sky-500" />
                  <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                    Filter
                  </span>
                </button>

                {showFilterPicker && (
                  <div
                    ref={filterPickerRef}
                    className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-3 shadow-lg z-10"
                  >
                    <p className="mb-2 text-xs font-medium text-gray-500">Stock filters</p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          checked={!!filters.lowStockOnly}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, lowStockOnly: e.target.checked }))
                          }
                        />
                        <span>Low stock</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          checked={!!filters.zeroStockOnly}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, zeroStockOnly: e.target.checked }))
                          }
                        />
                        <span>Zero stock</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          checked={!!filters.hasSamplesOnly}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, hasSamplesOnly: e.target.checked }))
                          }
                        />
                        <span>Has samples</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className={`relative ${isMobile ? "hidden" : ""}`}>
                <button
                  ref={columnPickerButtonRef}
                  type="button"
                  onClick={() => setShowColumnPicker((prev) => !prev)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 whitespace-nowrap"
                  title="Show/hide columns"
                >
                  <SlidersHorizontal className="w-4 h-4 text-sky-500" />
                  <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                    Columns
                  </span>
                </button>

                {showColumnPicker && (
                  <div
                    ref={columnPickerRef}
                    className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3"
                  >
                    <p className="text-xs font-medium text-gray-500 mb-2">Show / hide columns</p>

                    <div className="max-h-64 overflow-y-auto space-y-2 text-xs">
                      <div>
                        <p className="text-[11px] uppercase text-gray-400 mb-1">Standard</p>

                        {BUILT_IN_COLUMNS.map((col) => {
                          if (col.id === "price" && !canSeePricing) return null;

                          return (
                            <label
                              key={col.id}
                              className="flex items-center gap-2 text-gray-700 mb-1 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 text-blue-600 rounded border-gray-300"
                                checked={columnVisibility[col.id] !== false}
                                onChange={(e) => toggleColumn(col.id, e.target.checked)}
                              />
                              <span>{col.label}</span>
                            </label>
                          );
                        })}
                      </div>

                      {customColumns.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-[11px] uppercase text-gray-400 mb-1">Custom</p>

                          {customColumns.map((col) => (
                            <label
                              key={col.id}
                              className="flex items-center gap-2 text-gray-700 mb-1 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 text-blue-600 rounded border-gray-300"
                                checked={columnVisibility[col.id] !== false}
                                onChange={(e) => toggleColumn(col.id, e.target.checked)}
                              />
                              <span>{col.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {typeof onEditColumns === "function" ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowColumnPicker(false);
                          onEditColumns();
                        }}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit columns in Admin
                      </button>
                    ) : null}
                  </div>
                )}
              </div>

              {desktopActionsMenu ? <div className="shrink-0">{desktopActionsMenu}</div> : null}
              {desktopPrimaryAction ? <div className="shrink-0">{desktopPrimaryAction}</div> : null}

              <div className="ml-auto whitespace-nowrap text-xs font-semibold uppercase tracking-[0.16em]">
                <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  {filteredInventory.length} item{filteredInventory.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className={`min-w-full ${isMobile ? "text-[12px]" : "text-sm"}`}>
            <thead className="text-xs uppercase">
              <tr className="border-b border-sky-100">
                {visibleColumns.map((col) => (
                  <th key={col.id} className={getHeaderClass(col.id)}>
                    {renderHeader(col)}
                  </th>
                ))}
                {!isMobile && canManageInventory ? (
                  <th className="w-44 px-3 py-2 text-left border-r border-sky-100 last:border-r-0 text-slate-800 font-semibold bg-gradient-to-b from-sky-50 via-white to-white">
                    <button
                      type="button"
                      onClick={() => {
                        setAddColumnError("");
                        setShowAddColumnModal(true);
                      }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 hover:border-slate-400 hover:text-slate-700"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Column
                    </button>
                  </th>
                ) : null}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filteredInventory.map((item) => {
                const sampleCount = getProductSampleCount(item.id);
                const rowDim = item.unsampleable === true || item.archived === true;

                return (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${rowDim ? "opacity-60" : ""} ${
                      selectedIds.has(item.id) ? "bg-blue-50" : ""
                    }`}
                    title={rowDim ? "This item is marked unsampleable (grayed out)." : ""}
                  >
                    {visibleColumns.map((col) => (
                      <td key={col.id} className={getCellClass(col.id)}>
                        {renderCell(col, item, sampleCount)}
                      </td>
                    ))}
                    {!isMobile && canManageInventory ? (
                      <td className="w-44 px-3 py-1.5 text-xs text-slate-300"> </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredInventory.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No products found. Add your first item.
          </div>
        )}
      </div>

      {bulkEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Bulk Edit Inventory</div>
                <div className="text-xs text-slate-500 mt-1">
                  Update category, sample visibility, and customer visibility for {selectedIds.size} selected item{selectedIds.size === 1 ? "" : "s"}.
                </div>
              </div>
              <button
                type="button"
                onClick={closeBulkEdit}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Category</label>
                <select
                  value={bulkEditForm.category}
                  onChange={(e) => setBulkEditForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current category</option>
                  <option value="__clear__">Clear category</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Sample-able status</label>
                <select
                  value={bulkEditForm.sampleable}
                  onChange={(e) => setBulkEditForm((prev) => ({ ...prev, sampleable: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current sample-able setting</option>
                  <option value="sampleable">Make sample-able</option>
                  <option value="notSampleable">Make not sample-able</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Customer visibility</label>
                <select
                  value={bulkEditForm.customerVisibility}
                  onChange={(e) => setBulkEditForm((prev) => ({ ...prev, customerVisibility: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current customer visibility</option>
                  <option value="show">Show to customers</option>
                  <option value="hide">Hide from customers</option>
                </select>
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-emerald-900">Smart Categorize Items</div>
                    <div className="text-xs text-emerald-700 mt-1">
                      Review current category vs. recommended category from the SupplyHawk smart category knowledge base before applying changes.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={openSmartCategorize}
                    className="px-3 py-1.5 text-sm font-medium rounded-xl border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-100"
                  >
                    Review Suggestions
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={closeBulkEdit}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkEditSaving}
                onClick={runBulkEdit}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {bulkEditSaving ? "Saving…" : "Save Bulk Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddColumnModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <div className="text-sm font-semibold text-slate-900">Add Inventory Column</div>
                <div className="mt-1 text-xs text-slate-500">
                  Create a custom column and append it to the end of the table.
                </div>
              </div>
              <button
                type="button"
                onClick={resetAddColumnModal}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close add column modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Column Name
                </span>
                <input
                  type="text"
                  value={addColumnForm.label}
                  onChange={(e) =>
                    setAddColumnForm((prev) => ({ ...prev, label: e.target.value }))
                  }
                  placeholder="Ex. Cut Type"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Column Type
                </span>
                <select
                  value={addColumnForm.type}
                  onChange={(e) =>
                    setAddColumnForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes / No</option>
                  <option value="select">Dropdown</option>
                </select>
              </label>

              {addColumnError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {addColumnError}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={resetAddColumnModal}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateColumn}
                disabled={addingColumn}
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {addingColumn ? "Adding..." : "Add Column"}
              </button>
            </div>
          </div>
        </div>
      )}

      {smartCategorizeOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Smart Categorize Inventory</div>
                <div className="text-xs text-slate-500 mt-1">
                  Review what each selected item is today and what SupplyHawk recommends before applying category updates.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSmartCategorizeOpen(false)}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="max-h-[65vh] overflow-auto">
              {smartCategoryRows.length === 0 ? (
                <div className="p-6 text-sm text-slate-500">No category suggestions were found for the selected items.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px]">
                    <tr>
                      <th className="px-3 py-2 text-left">Select</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-left">What it was</th>
                      <th className="px-3 py-2 text-left">What I recommend</th>
                      <th className="px-3 py-2 text-left">Matched term</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {smartCategoryRows.map((row) => (
                      <tr key={row.itemId} className={smartSelectedIds.has(row.itemId) ? "bg-emerald-50/60" : "bg-white"}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={smartSelectedIds.has(row.itemId)}
                            onChange={() => toggleSmartRow(row.itemId)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900">{row.name}</div>
                          <div className="text-xs text-slate-500">{row.sku || "No SKU"}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.currentCombined}</td>
                        <td className="px-3 py-2 text-slate-900 font-semibold">{row.recommendedCombined}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{row.matchedTerm}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-3 bg-white">
              <div className="text-xs text-slate-500">
                {smartSelectedIds.size} suggestion{smartSelectedIds.size === 1 ? "" : "s"} selected to apply
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSmartCategorizeOpen(false)}
                  className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={bulkEditSaving || smartSelectedIds.size === 0}
                  onClick={approveSmartCategorization}
                  className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                >
                  {bulkEditSaving ? "Applying…" : "Approve Selected"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Product Parsing Modal */}
      {showBulkAnalysis &&
        bulkAnalysisProducts.length > 0 &&
        currentBulkIndex < bulkAnalysisProducts.length && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
            <div className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-2xl">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Wand2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Bulk Product Name Parser</h3>
                      <p className="text-sm text-gray-500">
                        Processing {bulkAnalysisProducts.length} selected product
                        {bulkAnalysisProducts.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowBulkAnalysis(false);
                      setBulkAnalysisProducts([]);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-blue-700 uppercase">Total</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">{bulkAnalysisProducts.length}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-purple-700 uppercase">Current</p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      {currentBulkIndex + 1} / {bulkAnalysisProducts.length}
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-medium text-gray-700 uppercase">Remaining</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {bulkAnalysisProducts.length - currentBulkIndex - 1}
                    </p>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentBulkIndex + 1) / bulkAnalysisProducts.length) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 text-right">
                  Product {currentBulkIndex + 1} of {bulkAnalysisProducts.length}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <ProductParsingPanel
                  product={bulkAnalysisProducts[currentBulkIndex]}
                  onApplyFix={handleParserApply}
                  onCancel={handleSkipProduct}
                  onAnalyzeAnother={() => {
                    if (currentBulkIndex + 1 < bulkAnalysisProducts.length) {
                      setCurrentBulkIndex((prev) => prev + 1);
                    }
                  }}
                  showMultiple={true}
                />
              </div>

              <div className="p-6 border-t border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      if (currentBulkIndex > 0) setCurrentBulkIndex((prev) => prev - 1);
                    }}
                    disabled={currentBulkIndex === 0}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <div className="text-sm text-gray-600">
                    {currentBulkIndex + 1} of {bulkAnalysisProducts.length} products
                  </div>

                  <button
                    type="button"
                    onClick={handleSkipProduct}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                  >
                    {currentBulkIndex + 1 < bulkAnalysisProducts.length ? "Skip & Next" : "Finish"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default InventoryTable;
